import React from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useFormikContext, getIn } from 'formik';

import Section from 'core/components/section';
import { FormField } from 'core/components/form';
import { appConfig, brandConstants } from 'brand-config';

// CFM Auto must be accompanied by a primary homeowner policy with CFM. Brand-gated so it renders only
// where the business rule applies; the fields live on the auto sub-object (values.auto), so the same
// component works in both the new-offer flow and the policy Details → Auto view.
//
// "Primary Homeowner policy with <brand>?" is a Yes/No dropdown (#35914, was a checkbox in #35661). It
// writes the Boolean auto.primaryResidentHomeowner, which the CFM ClarionDoor auto rater sends as
// primary_homeownerYN — the Homeowner discount. The answer also gates the offer: "No" or unanswered is
// a hard stop at Update Offer Price / Checkout / policy save, raised as a blocking validation error
// ("Must have Primary Homeowner Policy with CFM") in the auto validation schemas.
//
// The Companion Policy Type/Number identify that homeowner policy, so they apply only when the answer
// is Yes — they render (and are required, see the auto validation schemas) only then. Companion Policy
// Type is an independent staff selection, not derived from the homeowner answer: a Farm companion can
// include an owner-occupied dwelling, so "Home" alone would miss it.
const companionPolicyTypeOptions = [
  { id: 'Home', value: 'Home' },
  { id: 'Farm', value: 'Farm' }
];

// Boolean-valued so "No" (false) stays distinguishable from unanswered (undefined) — mirrors the
// YES_NO_OPTIONS select pattern used for the joint-ownership answers (#35809).
const yesNoOptions = [
  { id: true, value: 'Yes' },
  { id: false, value: 'No' }
];

const CompanionPolicy = ({ disabled }) => {
  const { values, initialValues, setFieldValue } = useFormikContext();
  if (!appConfig?.featureFlags?.collectAutoCompanionPolicy) {
    return null;
  }

  const hasPrimaryHomeownerPolicy = getIn(values, 'auto.primaryResidentHomeowner') === true;
  // A policy whose stored answer was already Yes keeps its stored Type/Number — don't wipe them on a
  // Yes→No→Yes toggle (the grandfather rule wouldn't re-require them, so clearing would lose them
  // silently — devlo on #35926).
  const hadStoredPrimaryHomeownerPolicy = getIn(initialValues, 'auto.primaryResidentHomeowner') === true;

  // When the answer moves off Yes, drop the companion details the user entered this session so a stale
  // Type/Number isn't carried on the offer/policy after the fields are hidden (they only describe a Yes
  // answer). Preserve a pre-existing/imported Yes's stored details (see above).
  const onPrimaryHomeownerChange = (nextValue) => {
    if (disabled || nextValue === true || hadStoredPrimaryHomeownerPolicy) {
      return;
    }
    setFieldValue('auto.companionPolicyType', null);
    setFieldValue('auto.companionPolicyNumber', null);
  };

  return (
    <Section title="Companion Policy" type="SubSection">
      <Grid container spacing={4}>
        <FormField
          name="auto.primaryResidentHomeowner"
          id="auto.primaryResidentHomeowner"
          type="select"
          label={`Primary Homeowner policy with ${brandConstants.SHORT_NAME}?`}
          mode="light"
          options={yesNoOptions}
          xs={4}
          // Boolean-backed selects need fast={false} so the field re-renders when the value changes
          // externally and this section's Yes-gated fields toggle in step (matches car.jsx #35809).
          fast={false}
          onChange={onPrimaryHomeownerChange}
          disabled={disabled}
        />
        {hasPrimaryHomeownerPolicy && (
          <>
            <FormField
              name="auto.companionPolicyType"
              id="auto.companionPolicyType"
              type="select"
              label="Companion Policy Type"
              mode="light"
              options={companionPolicyTypeOptions}
              xs={4}
              disabled={disabled}
            />
            <FormField
              name="auto.companionPolicyNumber"
              id="auto.companionPolicyNumber"
              type="string"
              label="Companion Policy Number"
              mode="light"
              xs={4}
              disabled={disabled}
            />
          </>
        )}
      </Grid>
    </Section>
  );
};

CompanionPolicy.propTypes = {
  disabled: PropTypes.bool
};

CompanionPolicy.defaultProps = {
  disabled: false
};

export default CompanionPolicy;
